/**
 * React Query hooks for remote tutoring pairs.
 * Source: spec-binomes.md §20-22.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import * as pairsService from '@/services/remote/pairs.service';
import { shouldRetryUnitGet } from '@/hooks/useUnitGetNotFound';
import type {
  PairRecord,
  PairsResponse,
  PairQueryParams,
  UpdatePairManagerDto,
  DissociatePairDto,
  CreatePairOneOffSessionDto,
  PairHistoryEvent,
} from '@/types/remote/pair.types';
import type { Session } from '@/types/inperson/session.types';
import { useAuthStore } from '@/stores/authStore';

/** Matches `sessionKeys.all` in useSessionsRemote — avoid importing that module here (Firebase side effects in node tests). */
const SESSIONS_REMOTE_QUERY_ROOT = ['sessions-remote'] as const;

/** React Query keys for remote pairs (`/companies/:companyId/pairs`). */
export const pairsKeys = {
  all: ['pairs'] as const,
  lists: () => [...pairsKeys.all, 'list'] as const,
  list: (companyId: string, params?: PairQueryParams) =>
    [...pairsKeys.lists(), companyId, { params }] as const,
  details: () => [...pairsKeys.all, 'detail'] as const,
  detail: (companyId: string, id: string) => [...pairsKeys.details(), companyId, id] as const,
  history: (companyId: string, id: string) => [...pairsKeys.all, 'history', companyId, id] as const,
};

/** `GET /companies/:companyId/pairs` — paginated pair list. */
export const usePairsList = (params?: PairQueryParams): UseQueryResult<PairsResponse> => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useQuery({
    queryKey: pairsKeys.list(companyId!, params),
    queryFn: () => pairsService.getPairs(companyId!, params),
    enabled: !!companyId,
  });
};

/** `GET /companies/:companyId/pairs/:id` */
export const usePair = (id: string | null | undefined): UseQueryResult<PairRecord> => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useQuery({
    queryKey: pairsKeys.detail(companyId!, id!),
    queryFn: () => pairsService.getPair(companyId!, id!),
    enabled: !!companyId && !!id,
    retry: shouldRetryUnitGet,
  });
};

/** `GET /companies/:companyId/pairs/:id/history` */
export const usePairHistory = (
  id: string | null | undefined,
  options?: { enabled?: boolean },
): UseQueryResult<PairHistoryEvent[]> => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useQuery({
    queryKey: pairsKeys.history(companyId!, id!),
    queryFn: () => pairsService.getPairHistory(companyId!, id!),
    enabled: !!companyId && !!id && options?.enabled !== false,
  });
};

/** `PATCH /companies/:companyId/pairs/:id/manager` — invalidates list, detail, history. */
export const useUpdatePairManager = (): UseMutationResult<
  PairRecord,
  Error,
  { pairId: string; dto: UpdatePairManagerDto }
> => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useMutation({
    mutationFn: ({ pairId, dto }) => pairsService.updatePairManager(companyId!, pairId, dto),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: pairsKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: pairsKeys.detail(companyId!, variables.pairId),
      });
      void queryClient.invalidateQueries({
        queryKey: pairsKeys.history(companyId!, variables.pairId),
      });
    },
  });
};

/** `POST /companies/:companyId/pairs/:id/dissociate` — invalidates list, detail, history. */
export const useDissociatePair = (): UseMutationResult<
  PairRecord,
  Error,
  { pairId: string; dto: DissociatePairDto }
> => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useMutation({
    mutationFn: ({ pairId, dto }) => pairsService.dissociatePair(companyId!, pairId, dto),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: pairsKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: pairsKeys.detail(companyId!, variables.pairId),
      });
      void queryClient.invalidateQueries({
        queryKey: pairsKeys.history(companyId!, variables.pairId),
      });
    },
  });
};

/** `GET /companies/:companyId/pairs/export` — download XLSX. */
export const useExportPairs = (): UseMutationResult<Blob, Error, PairQueryParams | undefined> => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useMutation({
    mutationFn: (params) => pairsService.exportPairsToExcel(companyId!, params),
  });
};

/** `POST /companies/:companyId/binomes/:pairId/sessions` — one-off session; invalidates pairs + sessions. */
export const useCreatePairOneOffSession = (): UseMutationResult<
  Session,
  Error,
  { pairId: string; dto: CreatePairOneOffSessionDto }
> => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useMutation({
    mutationFn: ({ pairId, dto }) => pairsService.createOneOffSession(companyId!, pairId, dto),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: pairsKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: pairsKeys.detail(companyId!, variables.pairId),
      });
      void queryClient.invalidateQueries({ queryKey: SESSIONS_REMOTE_QUERY_ROOT });
    },
  });
};
